import { useState, useCallback } from "react";
import { supabase } from "../../../../lib/supabase";
import { useApp } from "../../../../contexts/AppContext";
import { useCategories } from "../../../../hooks/useCategories";

export default function ProviderCategories() {
  const { currentUser } = useApp();
  const { categories, loading, refetch } = useCategories();
  const [showForm, setShowForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justAdded, setJustAdded] = useState<string | null>(null);
  const [error, setError] = useState("");

  const handleAdd = useCallback(async () => {
    if (!newName.trim()) { setError("カテゴリ名を入力してください。"); return; }
    if (!currentUser?.id) return;
    setSubmitting(true);
    setError("");
    const maxOrder = categories.length > 0 ? Math.max(...categories.map((c) => c.sort_order ?? 0)) : 0;
    const { error: err } = await supabase.from("categories").insert({
      name: newName.trim(),
      description: newDesc.trim() || null,
      icon: "ri-folder-line",
      visible: true,
      sort_order: maxOrder + 1,
      status: "approved",
      created_by: currentUser.id,
    });
    setSubmitting(false);
    if (err) {
      setError("追加に失敗しました。もう一度お試しください。");
      return;
    }
    setJustAdded(newName.trim());
    setNewName("");
    setNewDesc("");
    setShowForm(false);
    refetch();
    setTimeout(() => setJustAdded(null), 4000);
  }, [newName, newDesc, currentUser?.id, categories, refetch]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="font-display italic text-gold text-xs tracking-widest mb-1">Browse Categories</p>
          <h2 className="font-serif text-deep-green text-2xl font-light">カテゴリ一覧</h2>
          <p className="text-xs text-charcoal/50 font-sans mt-1">掲載されているカテゴリを閲覧・追加できます。</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-deep-green text-white text-sm px-5 py-2.5 rounded-full font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />カテゴリを追加する
        </button>
      </div>

      {/* Success banner */}
      {justAdded && (
        <div className="bg-teal/5 border border-teal/20 rounded-2xl p-4 flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal/10 flex-shrink-0">
            <i className="ri-checkbox-circle-line text-teal text-lg" />
          </div>
          <div>
            <p className="font-sans text-sm font-medium text-deep-green">「{justAdded}」を追加しました</p>
            <p className="text-xs text-charcoal/50 font-sans mt-0.5">
              カテゴリはすぐに全ユーザーに表示されます。管理者にも追加記録が残ります。
            </p>
          </div>
        </div>
      )}

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-2xl border border-teal/30 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 flex items-center justify-center rounded-full bg-teal/10">
              <i className="ri-folder-add-line text-teal" />
            </div>
            <div>
              <p className="font-serif text-deep-green text-base">新規カテゴリを追加</p>
              <p className="text-xs text-charcoal/40 font-sans">追加後すぐにカテゴリ一覧に反映されます</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">カテゴリ名 <span className="text-red-400">*</span></label>
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="例：革工芸・レザークラフト"
                maxLength={50}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal transition-colors"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAdd(); } }}
              />
            </div>
            <div>
              <label className="block text-xs font-sans text-charcoal/60 mb-1.5">説明文（任意）</label>
              <textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                placeholder="このカテゴリがどのような技術・分野を対象とするか（200文字以内）"
                maxLength={200}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border border-stone-200 text-sm font-sans focus:outline-none focus:border-teal resize-none transition-colors"
              />
              <p className="text-right text-xs text-charcoal/30 font-sans mt-0.5">{newDesc.length}/200</p>
            </div>
            {error && (
              <p className="text-xs text-red-500 font-sans bg-red-50 rounded-xl px-3 py-2 flex items-center gap-1.5">
                <i className="ri-error-warning-line" />{error}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setShowForm(false); setError(""); setNewName(""); setNewDesc(""); }}
                className="px-4 py-2.5 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 cursor-pointer hover:border-stone-300 whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={handleAdd}
                disabled={submitting || !newName.trim()}
                className="px-6 py-2.5 rounded-full bg-deep-green text-white text-sm font-sans cursor-pointer hover:bg-teal transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {submitting ? (
                  <span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin" />追加中...</span>
                ) : "追加する"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-2xl text-teal" />
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-16 text-charcoal/40 font-sans text-sm">
          <i className="ri-folder-line text-4xl block mb-3 text-charcoal/20" />
          カテゴリがまだありません
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => (
            <div
              key={cat.id}
              className="bg-white rounded-2xl border border-stone-100 p-5 hover:border-teal/30 transition-all duration-200 group"
            >
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-teal/8 group-hover:bg-teal/15 transition-colors flex-shrink-0">
                  <i className={`${cat.icon || "ri-folder-line"} text-deep-green text-xl`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm font-medium text-charcoal mb-1">{cat.name}</p>
                  {cat.description && (
                    <p className="text-xs text-charcoal/50 font-sans leading-relaxed">{cat.description}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
